import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  HStack,
  VStack,
  Icon,
  Button,
  useColorModeValue,
  Spinner,
  SimpleGrid,
  useToast
} from '@chakra-ui/react';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiRefreshCw,
} from 'react-icons/fi';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { useAuthStore } from '@/stores/authStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useTranslation } from "react-i18next";
import { durationToString } from '@/utils/utils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  variant?: 'primary' | 'success' | 'warning' | 'danger';
  trend?: {
    value: string;
    isPositive: boolean;
  };
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  subtitle,
  icon,
  variant = 'primary',
  trend
}) => {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const variantColors = {
    primary: { bg: 'blue.500', accent: 'blue.100' },
    success: { bg: 'green.500', accent: 'green.100' },
    warning: { bg: 'orange.500', accent: 'orange.100' },
    danger: { bg: 'red.500', accent: 'red.100' }
  };

  const colors = variantColors[variant];

  return (
    <Card
      bg={bg}
      borderColor={borderColor}
      borderWidth="1px"
      borderRadius="xl"
      shadow="sm"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        height="4px"
        bgGradient={`linear(to-r, ${colors.bg}, ${colors.bg})`}
      />
      
      <CardBody p={6}>
        <Flex justify="space-between" align="flex-start" mb={4}>
          <VStack align="flex-start" spacing={2} flex="1">
            <HStack spacing={3}>
              <Box
                p={3}
                borderRadius="lg"
                bg={colors.accent}
                fontSize="24px"
              >
                {icon}
              </Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                {title}
              </Text>
            </HStack>
            
            <Text fontSize="3xl" fontWeight="bold" lineHeight="1">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Text>
            
            {subtitle && (
              <Text fontSize="sm" color="gray.500">
                {subtitle}
              </Text>
            )}
            
            {trend && (
              <HStack spacing={1}>
                <Icon 
                  as={trend.isPositive ? FiTrendingUp : FiTrendingDown}
                  color={trend.isPositive ? 'green.500' : 'red.500'}
                  boxSize={4}
                />
                <Text 
                  fontSize="sm" 
                  color={trend.isPositive ? 'green.500' : 'red.500'}
                  fontWeight="medium"
                >
                  {trend.value}
                </Text>
              </HStack>
            )}
          </VStack>
        </Flex>
      </CardBody>
    </Card>
  );
};

const HOUR_THRESHOLDS = {
  short: 2 * 3600,
  medium: 6 * 3600,
  long: 12 * 3600,
};

const CHART_COLORS = [
  '#e74c3c', '#3498db', '#27ae60', '#f39c12', '#9b59b6',
  '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#2ecc71'
];

const HOURLY_RATE = 70;

interface DurationBucket {
  count: number;
  totalSeconds: number;
}

function computeDurationBuckets(durations: number[]): DurationBucket[] {
  const buckets: DurationBucket[] = [
    { count: 0, totalSeconds: 0 },
    { count: 0, totalSeconds: 0 },
    { count: 0, totalSeconds: 0 },
    { count: 0, totalSeconds: 0 },
  ];
  for (const d of durations) {
    const idx = d <= HOUR_THRESHOLDS.short ? 0
      : d <= HOUR_THRESHOLDS.medium ? 1
      : d <= HOUR_THRESHOLDS.long ? 2
      : 3;
    buckets[idx].count++;
    buckets[idx].totalSeconds += d;
  }
  return buckets;
}

interface HourlyCategory {
  label: string;
  hours: string;
  total: number;
}

function computeHourlyCategories(
  hourlyStats: Record<number, number>,
  t: (key: string) => string
): HourlyCategory[] {
  const entries = Object.entries(hourlyStats)
    .map(([hour, count]) => ({ hour: parseInt(hour), count: count || 0 }));
  
  const sortedDesc = [...entries].sort((a, b) => b.count - a.count);
  const sortedAsc = [...entries].sort((a, b) => a.count - b.count);

  const formatHours = (items: typeof entries) =>
    items.map(h => `${h.hour.toString().padStart(2, '0')}:00`).join(', ');
  const sumCounts = (items: typeof entries) =>
    items.reduce((sum, h) => sum + h.count, 0);

  const peakHours = sortedDesc.slice(0, 3);
  const busyHours = sortedDesc.slice(3, 6);
  const inactiveHours = sortedAsc.slice(0, 3);

  const businessHourRange = Array.from({ length: 9 }, (_, i) => i + 9);
  const businessTotal = businessHourRange.reduce((sum, hour) =>
    sum + (hourlyStats[hour] || 0), 0);

  return [
    {
      label: `🔥 ${t('dashboard.analytics.charts.peakHours')}`,
      hours: formatHours(peakHours),
      total: sumCounts(peakHours),
    },
    {
      label: `⚡ ${t('dashboard.analytics.charts.busyHours')}`,
      hours: formatHours(busyHours),
      total: sumCounts(busyHours),
    },
    {
      label: `💼 ${t('dashboard.analytics.charts.businessHours')}`,
      hours: '09:00 - 17:00',
      total: businessTotal,
    },
    {
      label: `🌙 ${t('dashboard.analytics.charts.inactiveHours')}`,
      hours: formatHours(inactiveHours),
      total: sumCounts(inactiveHours),
    },
  ];
}

type TimeframeOption = 'day' | 'week' | 'month' | 'all';

function getTimeframeStart(timeframe: TimeframeOption, now: Date): Date | null {
  switch (timeframe) {
    case 'day':
      return new Date(now.getTime() - 24 * 60 * 60 * 1000);
    case 'week':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    case 'month':
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    case 'all':
    default:
      return null;
  }
}

const CATEGORY_COLORS = [
  { bg: 'red.50', border: 'red.500', text: 'red.700' },
  { bg: 'orange.50', border: 'orange.500', text: 'orange.700' },
  { bg: 'green.50', border: 'green.500', text: 'green.700' },
  { bg: 'blue.50', border: 'blue.500', text: 'blue.700' },
];

export const AnalyticsReports: React.FC = () => {
  const { user, token } = useAuthStore();
  const {
    parkingSessions,
    devices,
    payments,
    isLoadingParkingSessions,
    isLoadingDevices,
    isLoadingPayments,
    fetchAllDashboardData,
  } = useDashboardStore();
  const toast = useToast();
  const { t } = useTranslation();
  const [timeframe, setTimeframe] = useState<TimeframeOption>('all');

  const isLoading = isLoadingParkingSessions || isLoadingDevices || isLoadingPayments;

  const fetchData = async () => {
    if (!token || !user) return;

    try {
      const isSystem = user.role === 'SYSTEM';
      await fetchAllDashboardData(isSystem, user.tenantId);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast({
        title: 'Data loading error',
        description: 'An error occurred while loading analytics data.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [user, token]);

  const metrics = useMemo(() => {
    if (!parkingSessions.length && !devices.length) return null;

    const now = new Date();
    const timeframeStart = getTimeframeStart(timeframe, now);
    const filteredParkingSessions = parkingSessions.filter((session) => {
      if (!timeframeStart) return true;
      if (!session.start) return false;
      const sessionStart = new Date(session.start);
      return !Number.isNaN(sessionStart.getTime()) && sessionStart >= timeframeStart;
    });
    const filteredPayments = payments.filter((payment: any) => {
      if (!timeframeStart) return true;
      if (!payment?.created_at) return false;
      if (payment.status !== 'CAPTURED') return false;
      const paymentDate = new Date(payment.created_at);
      return !Number.isNaN(paymentDate.getTime()) && paymentDate >= timeframeStart;
    });

    const statusStats: Record<string, number> = {};
    const hourlyStats: Record<number, number> = {};
    const deviceStats: Record<string, number> = {};
    const durations: number[] = [];

    for (const session of filteredParkingSessions) {
      statusStats[session.status] = (statusStats[session.status] || 0) + 1;

      if (session.start) {
        const hour = new Date(session.start).getHours();
        hourlyStats[hour] = (hourlyStats[hour] || 0) + 1;
      }

      if (session.start && session.end) {
        const dur = (new Date(session.end).getTime() - new Date(session.start).getTime()) / 1000;
        durations.push(dur);
      }

      const device = devices.find((d: any) => d.id === session.device_id);
      const deviceName = device
        ? (device.nick_name || device.serial_no || `Device ${device.id}`)
        : 'Unknown Device';
      deviceStats[deviceName] = (deviceStats[deviceName] || 0) + 1;
    }

    const totalDurationSeconds = durations.reduce((a, b) => a + b, 0);
    const avgDuration = durations.length > 0 ? Math.floor(totalDurationSeconds / durations.length) : 0;
    const totalRevenue = filteredPayments.reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0);
    const durationBuckets = computeDurationBuckets(durations);

    return {
      deviceStats,
      statusStats,
      durations,
      avgDuration,
      totalDurationSeconds,
      totalRevenue,
      hourlyStats,
      durationBuckets,
      totalSessions: filteredParkingSessions.length,
      activeSessions: statusStats.ACTIVE || 0,
      completedSessions: statusStats.COMPLETED || 0,
    };
  }, [parkingSessions, devices, payments, timeframe]);

  if (isLoading || !metrics) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">{t('dashboard.loadingText')}</Text>
      </Box>
    );
  }

  const deviceChartData = {
    labels: Object.keys(metrics.deviceStats),
    datasets: [{
      data: Object.values(metrics.deviceStats),
      backgroundColor: CHART_COLORS,
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const statusChartData = {
    labels: Object.keys(metrics.statusStats),
    datasets: [{
      data: Object.values(metrics.statusStats),
      backgroundColor: ['#27ae60', '#f39c12', '#3498db', '#e74c3c'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  const hourlyChartData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [{
      label: 'Parking Count',
      data: Array.from({ length: 24 }, (_, i) => metrics.hourlyStats[i] || 0),
      backgroundColor: 'rgba(52, 152, 219, 0.2)',
      borderColor: 'rgba(52, 152, 219, 1)',
      borderWidth: 2,
      fill: true
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true
        }
      }
    }
  };

  const hourlyCategories = computeHourlyCategories(metrics.hourlyStats, t);

  const durationBucketLabels = [
    { range: `0-2 ${t('common.time')}`, label: t('dashboard.analytics.charts.shortTermParking') },
    { range: `2-6 ${t('common.time')}`, label: t('dashboard.analytics.charts.mediumTermParking') },
    { range: `6-12 ${t('common.time')}`, label: t('dashboard.analytics.charts.longTermParking') },
    { range: `12+ ${t('common.time')}`, label: t('dashboard.analytics.charts.extraLongParking') },
  ];

  const expectedRevenue = Math.floor(metrics.totalDurationSeconds / 3600) * HOURLY_RATE;

  return (
    <Box p={6} maxW="1400px" mx="auto">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Box>
          <Heading size="xl" mb={2}>
            📊 {t('dashboard.analytics.title')}
          </Heading>
          <Text color="gray.600">
            {t('dashboard.analytics.subtitle')}
          </Text>
        </Box>
        <VStack align="flex-end" spacing={2}>
          <HStack spacing={2}>
            <Button
              size="sm"
              variant={timeframe === 'day' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => setTimeframe('day')}
            >
              {t('dashboard.analytics.timeframes.day')}
            </Button>
            <Button
              size="sm"
              variant={timeframe === 'week' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => setTimeframe('week')}
            >
              {t('dashboard.analytics.timeframes.week')}
            </Button>
            <Button
              size="sm"
              variant={timeframe === 'month' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => setTimeframe('month')}
            >
              {t('dashboard.analytics.timeframes.month')}
            </Button>
            <Button
              size="sm"
              variant={timeframe === 'all' ? 'solid' : 'outline'}
              colorScheme="blue"
              onClick={() => setTimeframe('all')}
            >
              {t('dashboard.analytics.timeframes.allTime')}
            </Button>
          </HStack>
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchData}
            isLoading={isLoading}
            loadingText={t('dashboard.analytics.refreshing')}
            colorScheme="blue"
            variant="outline"
            size="sm"
          >
            {t('dashboard.analytics.refresh')}
          </Button>
        </VStack>
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList mb={6}>
          <Tab>📈 {t('dashboard.analytics.tabs.executiveSummary')}</Tab>
          <Tab>⏰ {t('dashboard.analytics.tabs.timeAnalysis')}</Tab>
          <Tab>⏱️ {t('dashboard.analytics.tabs.durationAnalysis')}</Tab>
        </TabList>

        <TabPanels>
          {/* Executive Summary */}
          <TabPanel p={0}>
            <VStack spacing={8} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <KPICard
                  title={t('dashboard.analytics.kpi.totalParkings')}
                  value={metrics.totalSessions}
                  subtitle={t('dashboard.analytics.kpi.allParkings')}
                  icon="🚗"
                  variant="primary"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.activeParking')}
                  value={metrics.activeSessions}
                  subtitle={t('dashboard.analytics.kpi.ongoingParkings')}
                  icon="🟢"
                  variant="success"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.totalRevenue')}
                  value={`₺${metrics.totalRevenue.toLocaleString()} / ₺${expectedRevenue.toLocaleString()}`}
                  subtitle={t('dashboard.analytics.kpi.allPayments')}
                  icon="💰"
                  variant="warning"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.averageDuration')}
                  value={durationToString(metrics.avgDuration)}
                  subtitle={t('dashboard.analytics.kpi.parkingDuration')}
                  icon="⏱️"
                  variant="danger"
                />
              </SimpleGrid>

              <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={8}>
                <Card>
                  <CardHeader>
                    <Heading size="md">{t('dashboard.analytics.charts.parkingsPerDevice')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <Box height="300px">
                      <Doughnut data={deviceChartData} options={chartOptions} />
                    </Box>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <Heading size="md">{t('dashboard.analytics.charts.parkingStatus')}</Heading>
                  </CardHeader>
                  <CardBody>
                    <Box height="300px">
                      <Pie data={statusChartData} options={chartOptions} />
                    </Box>
                  </CardBody>
                </Card>
              </Grid>

              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.deviceUsage')}</Heading>
                </CardHeader>
                <CardBody>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('dashboard.analytics.charts.deviceName')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.numberOfParkings')}</Th>
                          <Th>{t('dashboard.analytics.charts.status')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.parkingShare')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {Object.entries(metrics.deviceStats).map(([device, count]: [string, any]) => (
                          <Tr key={device}>
                            <Td fontWeight="medium">{device}</Td>
                            <Td isNumeric>{count}</Td>
                            <Td>
                              <Badge colorScheme="green" size="sm">{t('common.active')}</Badge>
                            </Td>
                            <Td isNumeric>
                              {((count / metrics.totalSessions) * 100).toFixed(1)}%
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* Timeline Analysis */}
          <TabPanel p={0}>
            <VStack spacing={8} align="stretch">
              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.hourlyParkings')}</Heading>
                </CardHeader>
                <CardBody>
                  <Box height="400px">
                    <Line data={hourlyChartData} options={{
                      ...chartOptions,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: t('dashboard.analytics.charts.transactionCount')
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: t('dashboard.analytics.charts.hour')
                          }
                        }
                      }
                    }} />
                  </Box>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <Heading size="md">🕐 {t('dashboard.analytics.charts.hourlyTrafficAnalysis')}</Heading>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                    {hourlyCategories.map((cat, i) => (
                      <Box
                        key={i}
                        p={6}
                        borderRadius="lg"
                        bg={CATEGORY_COLORS[i].bg}
                        borderLeft="4px solid"
                        borderLeftColor={CATEGORY_COLORS[i].border}
                      >
                        <Heading size="sm" color={CATEGORY_COLORS[i].text} mb={2}>
                          {cat.label}
                        </Heading>
                        <Text fontWeight="bold">{cat.hours}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {cat.total} {t('dashboard.analytics.charts.transactions')}
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>

          {/* Duration Analysis */}
          <TabPanel p={0}>
            <VStack spacing={8} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <KPICard
                  title={t('dashboard.analytics.kpi.averageDuration')}
                  value={durationToString(metrics.avgDuration)}
                  subtitle={t('dashboard.analytics.kpi.optimalLevel')}
                  icon="⏱️"
                  variant="primary"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.longestParking')}
                  value={metrics.durations.length ? durationToString(Math.max(...metrics.durations)) : '-'}
                  subtitle={t('dashboard.analytics.kpi.maximumDuration')}
                  icon="🏆"
                  variant="success"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.shortestParking')}
                  value={metrics.durations.length ? durationToString(Math.min(...metrics.durations)) : '-'}
                  subtitle={t('dashboard.analytics.kpi.minimumDuration')}
                  icon="⚡"
                  variant="warning"
                />
                <KPICard
                  title={t('dashboard.analytics.kpi.totalParkingHours')}
                  value={durationToString(Math.floor(metrics.totalDurationSeconds))}
                  subtitle={t('dashboard.analytics.kpi.cumulativeDuration')}
                  icon="📊"
                  variant="danger"
                />
              </SimpleGrid>

              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.parkingDuration')}</Heading>
                </CardHeader>
                <CardBody>
                  <Box height="400px">
                    <Bar data={{
                      labels: durationBucketLabels.map(b => b.label),
                      datasets: [{
                        label: 'Parking Count',
                        data: metrics.durationBuckets.map(b => b.count),
                        backgroundColor: ['#3498db', '#27ae60', '#f39c12', '#e74c3c'],
                        borderWidth: 2,
                        borderColor: '#fff'
                      }]
                    }} options={{
                      ...chartOptions,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: t('dashboard.analytics.charts.transactionCount')
                          }
                        }
                      }
                    }} />
                  </Box>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <Heading size="md">{t('dashboard.analytics.charts.durationAnalysis')}</Heading>
                </CardHeader>
                <CardBody>
                  <TableContainer>
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('dashboard.analytics.charts.durationRange')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.transactionCount')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.totalDuration')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.revenue')}</Th>
                          <Th isNumeric>{t('dashboard.analytics.charts.parkingNumberShare')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {metrics.durationBuckets.map((bucket, i) => (
                          <Tr key={i}>
                            <Td>
                              <strong>{durationBucketLabels[i].range}</strong>
                              <br />
                              <Text fontSize="sm" color="gray.500">{durationBucketLabels[i].label}</Text>
                            </Td>
                            <Td isNumeric>{bucket.count}</Td>
                            <Td isNumeric>{durationToString(Math.floor(bucket.totalSeconds))}</Td>
                            <Td isNumeric>
                              {Math.round(HOURLY_RATE * bucket.totalSeconds / 3600).toLocaleString()}TL
                            </Td>
                            <Td isNumeric>
                              {metrics.durations.length > 0
                                ? ((bucket.count / metrics.durations.length) * 100).toFixed(1)
                                : '0.0'}%
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
